import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import RecordRTC from 'recordrtc';
import { usePreferences } from '../context/PreferencesContext';
import { shouldRelaxDisplayCapture } from '../utils/displayCaptureRelaxed';
import { createCropPipeline, type CropPipeline } from '../lib/capture/cropPipeline';
import { createCompositor, DEFAULT_CAMERA_BUBBLE, type CameraBubble, type Compositor } from '../lib/capture/compositor';
import { toast } from '../lib/toast';
import i18n from '../lib/i18n';

export interface CameraPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type RecordingMode = 'fullScreen' | 'selectArea';

export interface RecordingOptions {
  recordScreen: boolean;
  recordAudio: boolean;
  recordCamera: boolean;
  cameraPosition?: CameraPosition;
  /** Shape, size and corner of the camera bubble drawn into the video. */
  cameraBubble?: CameraBubble;
  recordingMode: RecordingMode;
  selectedArea?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export const useRecording = () => {
  const { preferences } = usePreferences();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isMicOn, setIsMicOn] = useState(true);
  // The recording has a microphone (its button can mute / unmute it).
  const [hasMic, setHasMic] = useState(false);

  const recorderRef = useRef<RecordRTC | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Screen + camera compositor (camera sessions only), for showing/hiding the bubble.
  const compositorRef = useRef<Compositor | null>(null);
  const [hasCamera, setHasCamera] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(true);
  // Live camera stream (for the on-screen preview) and what kind of surface is captured
  // ('monitor' = full screen, 'window', 'browser' = tab).
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [captureSurface, setCaptureSurface] = useState<string | null>(null);
  // Bubble visibility = the user's choice AND not suppressed (a preview window that is itself
  // being captured already shows the camera).
  const isCameraOnRef = useRef(true);
  const bubbleSuppressedRef = useRef(false);
  const applyBubbleVisibility = useCallback(() => {
    compositorRef.current?.setCameraVisible(isCameraOnRef.current && !bubbleSuppressedRef.current);
  }, []);
  // The raw screen capture (released on stop, so the browser's "sharing" indicator ends)
  // and the selectArea crop that reads from it.
  const screenStreamRef = useRef<MediaStream | null>(null);
  const cropPipelineRef = useRef<CropPipeline | null>(null);
  // Mixes the microphone with the tab / system sound (the recorder keeps one audio track).
  const audioMixRef = useRef<AudioContext | null>(null);
  // Refs so callbacks registered at start (e.g. the browser's "Stop sharing") see current state.
  const isRecordingRef = useRef(false);
  const stopPromiseRef = useRef<Promise<Blob> | null>(null);
  const stopRecordingRef = useRef<(() => Promise<Blob>) | null>(null);

  /** Stops the screen capture and the crop. Safe to call more than once. */
  const releaseCapture = useCallback(() => {
    cropPipelineRef.current?.stop();
    cropPipelineRef.current = null;
    compositorRef.current = null; // stopped above (it is also the session's pipeline)
    screenStreamRef.current?.getTracks().forEach((track) => track.stop());
    screenStreamRef.current = null;
    void audioMixRef.current?.close().catch(() => undefined);
    audioMixRef.current = null;
  }, []);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // The recorded time is read from the clock, never counted in ticks: the browser slows the timers
  // of a background tab (the usual case while another window is recorded), and a counted second
  // that does not fire is lost — a 3:32 recording was saved as 1:46. The interval only refreshes
  // the display. Time recorded before the current run (pauses excluded), and when that run began:
  const recordedMsRef = useRef(0);
  const runStartRef = useRef<number | null>(null);

  const startTimer = useCallback(() => {
    runStartRef.current = performance.now();
    timerRef.current = setInterval(() => {
      const start = runStartRef.current;
      if (start !== null) setRecordingTime(Math.floor((recordedMsRef.current + performance.now() - start) / 1000));
    }, 250);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (runStartRef.current !== null) {
      recordedMsRef.current += performance.now() - runStartRef.current;
      runStartRef.current = null;
      setRecordingTime(Math.floor(recordedMsRef.current / 1000));
    }
  }, []);

  // Unmount (recorder closed, page left): nothing will use the recording any more, so stop the
  // recorder and release everything — screen, crop, microphone, camera, timer — so the
  // browser's sharing / mic indicators end.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      const recorder = recorderRef.current;
      recorderRef.current = null;
      isRecordingRef.current = false;
      if (recorder) {
        try { recorder.stopRecording(() => { /* discarded */ }); } catch { /* already stopped */ }
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
      audioStreamRef.current?.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
      releaseCapture();
    };
  }, [releaseCapture]);

  const storeRecordingOptions = useCallback((options: RecordingOptions) => {
    try {
      localStorage.setItem('recordingOptions', JSON.stringify({
        recordScreen: options.recordScreen,
        recordAudio: options.recordAudio,
        recordCamera: options.recordCamera,
        cameraPosition: options.cameraPosition,
        recordingMode: options.recordingMode,
        selectedArea: options.selectedArea ? { ...options.selectedArea } : undefined,
        timestamp: Date.now(),
      }));
    } catch (err) {
      console.error('[useRecording] Error storing recording options:', err);
    }
  }, []);

  // Get the stored area selection coordinates if available
  const getStoredAreaSelection = useCallback(() => {
    try {
      const storedOptions = localStorage.getItem('recordingOptions');
      if (!storedOptions) return null;
      const opts = JSON.parse(storedOptions) as Partial<RecordingOptions> & { timestamp?: number };
      if (opts.recordingMode === 'selectArea' && opts.selectedArea) {
        return { ...opts.selectedArea };
      }
    } catch (err) {
      console.error('[useRecording] Error getting stored area selection:', err);
    }
    return null;
  }, []);
  
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== 'recordingOptions' || !event.newValue) return;
      // Storage listener kept for cross-tab awareness; no action needed during active recording
      // since frozenArea is captured once at recording start.
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const audioConstraints = useMemo(
    () => ({
      echoCancellation: preferences.audio.echoCancellation,
      noiseSuppression: preferences.audio.noiseSuppression,
      autoGainControl: preferences.audio.autoGainControl,
      sampleRate: 44100,
    }),
    [preferences.audio]
  );

  const shouldCaptureSystemAudio = preferences.audio.systemAudio;

  /** Stops everything a start acquired (screen, mic, camera) when nothing ends up recording. */
  const abandonCapture = useCallback(() => {
    releaseCapture();
    audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    audioStreamRef.current = null;
    cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
    setCameraStream(null);
    setHasCamera(false);
    setHasMic(false);
  }, [releaseCapture]);

  /**
   * Resolves true when recording started; false on failure (see `error`) or cancel.
   *
   * `beforeStart` runs once everything is captured and ready, right before the recorder starts
   * (e.g. a countdown after the screen was picked). Resolving false cancels: nothing is recorded
   * and the capture is released. Its signal aborts when the user stops sharing meanwhile.
   */
  const startRecording = useCallback(async (
    options: RecordingOptions,
    preSelectedScreenStream?: MediaStream,
    beforeStart?: (signal: AbortSignal) => Promise<boolean>,
  ): Promise<boolean> => {
    try {
      setError(null);
      const streams: MediaStream[] = [];
      releaseCapture(); // leftovers from a prior session
      // A microphone of the previous recording (stopped by then) isn't this one's.
      audioStreamRef.current = null;
      let cameraStream: MediaStream | null = null;
      let screenStream: MediaStream | null = null;

      let microphoneAdded = false;

      // Store recording options for cross-tab access
      storeRecordingOptions(options);

      // Screen capture first (permissions in user-gesture chain from "Start" / picker / "Confirm").
      if (options.recordScreen) {
        // Check if we have a pre-selected screen stream
        if (preSelectedScreenStream) {
          console.log('[useRecording] Using pre-selected screen stream');
          screenStream = preSelectedScreenStream;
        } else {
          const relaxedEnv = shouldRelaxDisplayCapture();
          const relaxedVideo: Record<string, unknown> = { frameRate: { ideal: 30 } };

          const getDisplayMediaSafe = async (strictVideo: Record<string, unknown>) => {
            const invoke = async (video: Record<string, unknown>) =>
              (navigator.mediaDevices as unknown as {
                getDisplayMedia: (c?: unknown) => Promise<MediaStream>;
              }).getDisplayMedia({
                video,
                audio: shouldCaptureSystemAudio,
                // Offer the current tab in the picker (Chrome may hide it by default).
                selfBrowserSurface: 'include',
              });
            if (relaxedEnv) {
              return invoke(relaxedVideo);
            }
            try {
              return await invoke(strictVideo);
            } catch (firstErr) {
              console.warn('[useRecording] getDisplayMedia strict failed, retry relaxed:', firstErr);
              return invoke(relaxedVideo);
            }
          };

          // For full screen mode
          if (options.recordingMode === 'fullScreen') {
            const strictVideoFs: Record<string, unknown> = {
              frameRate: { ideal: 30 },
              displaySurface: 'monitor',
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            };
            console.log('[useRecording] Starting full screen recording');
            screenStream = await getDisplayMediaSafe(strictVideoFs);
            if (!screenStream) {
              throw new Error('Failed to get screen stream for full screen recording');
            }
          } else if (options.recordingMode === 'selectArea') {
            const areaSelection = options.selectedArea || getStoredAreaSelection();

            if (areaSelection) {
              if (!options.selectedArea) {
                options = { ...options, selectedArea: { ...areaSelection } };
                storeRecordingOptions(options);
              }

              const strictVideoArea: Record<string, unknown> = {
                frameRate: { ideal: 30 },
                displaySurface: 'monitor',
                width: { ideal: window.screen.width },
                height: { ideal: window.screen.height },
              };

              screenStream = await getDisplayMediaSafe(strictVideoArea);

              if (!screenStream) {
                throw new Error('Failed to get screen stream for area selection');
              }
            } else {
              throw new Error('Area selection mode active but no area coordinates found');
            }
          }
        }

        /*
         * Request microphone HERE (before selectArea canvas + video.play()).
         * Full-screen flow reached getUserMedia after a successful getDisplayMedia; select-area pushed
         * canvas work BEFORE mic, so a failing video.play() aborted startRecording and the browser
         * never prompted for microphone — unlike "Full Screen".
         */
        if (options.recordAudio) {
          try {
            const audioStream = await navigator.mediaDevices.getUserMedia({
              audio: audioConstraints,
              video: false,
            });
            streams.push(audioStream);
            audioStreamRef.current = audioStream;
            microphoneAdded = true;
          } catch (audioError) {
            console.warn('Could not access microphone:', audioError);
            setError(
              'Microphone access denied. Recording will continue without microphone audio.',
            );
          }
        }

        if (screenStream) {
          screenStreamRef.current = screenStream;
          const surface = screenStream.getVideoTracks()[0]?.getSettings?.().displaySurface;
          setCaptureSurface(typeof surface === 'string' ? surface : null);
          // The browser's "Stop sharing" (or closing the shared window) ends the capture:
          // stop the recorder so the video is kept instead of recording a frozen/black source.
          screenStream.getVideoTracks().forEach((track: MediaStreamTrack) => {
            track.addEventListener('ended', () => {
              if (isRecordingRef.current && stopRecordingRef.current) {
                stopRecordingRef.current().catch((err) =>
                  console.error('[useRecording] stop after capture ended failed:', err),
                );
              }
            });
          });
        }

        // Camera: drawn into the video by the compositor, so it's recorded in every mode.
        if (options.recordCamera && screenStream) {
          try {
            cameraStream = await navigator.mediaDevices.getUserMedia({
              video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 30 } },
              audio: false, // the microphone is captured separately
            });
            cameraStreamRef.current = cameraStream;
          } catch (cameraError) {
            console.warn('Could not access camera:', cameraError);
            toast.warning(i18n.t('camera_unavailable_recording', 'Camera not available — recording without camera.'));
          }
        }

        const area = options.recordingMode === 'selectArea' ? options.selectedArea : undefined;
        if (cameraStream && screenStream) {
          const compositor = createCompositor(screenStream, cameraStream, {
            area,
            bubble: options.cameraBubble ?? DEFAULT_CAMERA_BUBBLE,
          });
          cropPipelineRef.current = compositor;
          compositorRef.current = compositor;
          isCameraOnRef.current = true;
          bubbleSuppressedRef.current = false;
          setHasCamera(true);
          setIsCameraOn(true);
          setCameraStream(cameraStream);
          streams.push(compositor.stream);
          // System/tab audio travels next to the composited video.
          if (screenStream.getAudioTracks().length > 0) {
            streams.push(new MediaStream(screenStream.getAudioTracks()));
          }
        } else if (options.recordingMode === 'selectArea' && options.selectedArea && screenStream) {
          // PreviewAndCrop already converts the overlay into stream pixels (videoWidth x
          // videoHeight); don't rescale with window.screen (window/tab captures are smaller).
          const pipeline = createCropPipeline(screenStream, options.selectedArea);
          cropPipelineRef.current = pipeline;
          streams.push(pipeline.stream);
          // System/tab audio travels next to the cropped video.
          if (screenStream.getAudioTracks().length > 0) {
            streams.push(new MediaStream(screenStream.getAudioTracks()));
          }
        } else if (options.recordingMode === 'fullScreen' && screenStream) {
          // For full screen recording, use the stream directly
          streams.push(screenStream);
        }
      }

      // Microphone for audio-only sessions (no screen capture)
      if (options.recordAudio && !microphoneAdded) {
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({
            audio: audioConstraints,
            video: false
          });
          streams.push(audioStream);
          audioStreamRef.current = audioStream;
        } catch (audioError) {
          console.warn('Could not access microphone:', audioError);
          setError('Microphone access denied. Recording will continue without microphone audio.');
        }
      }

      if (streams.length === 0) {
        throw new Error('No recording sources available');
      }

      // Combine all streams. The recorder keeps only the first audio track, so the microphone
      // and the tab / system sound are mixed into one (muting the mic still works: its track).
      const tracks = streams.flatMap((stream: MediaStream) => stream.getTracks());
      const audioTracks = tracks.filter((track) => track.kind === 'audio');
      const combinedStream = new MediaStream(tracks.filter((track) => track.kind !== 'audio'));
      if (audioTracks.length > 1) {
        const mix = new AudioContext();
        audioMixRef.current = mix;
        void mix.resume().catch(() => undefined);
        const destination = mix.createMediaStreamDestination();
        audioTracks.forEach((track) => mix.createMediaStreamSource(new MediaStream([track])).connect(destination));
        destination.stream.getAudioTracks().forEach((track) => combinedStream.addTrack(track));
      } else {
        audioTracks.forEach((track) => combinedStream.addTrack(track));
      }

      streamRef.current = combinedStream;
      setHasMic(!!audioStreamRef.current);
      setIsMicOn(true);

      // Detect the best available codec at runtime.
      // vp9+opus is preferred (best compression in WebM); fall back to vp8+opus,
      // then plain WebM if neither is supported (e.g. some Firefox builds).
      const bestMimeType =
        MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
          ? 'video/webm;codecs=vp9,opus'
          : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ? 'video/webm;codecs=vp8,opus'
          : 'video/webm';

      // Create recorder with screencasting-optimised settings (Option B).
      // 1.5 Mbps is the sweet spot for screen + cursor content with VP9 — ~30-40%
      // smaller than the previous 2.5 Mbps CBR without perceptible quality loss.
      // frameInterval removed so the browser manages keyframes adaptively.
      const outputWidth =
        options.recordingMode === 'selectArea' && options.selectedArea
          ? options.selectedArea.width
          : 1920;
      const outputHeight =
        options.recordingMode === 'selectArea' && options.selectedArea
          ? options.selectedArea.height
          : 1080;

      if (beforeStart) {
        // "Stop sharing" during the wait ends it at once instead of when it runs out.
        const sharing = new AbortController();
        const onEnded = () => sharing.abort();
        const videoTracks = screenStream?.getVideoTracks() ?? [];
        videoTracks.forEach((track) => track.addEventListener('ended', onEnded));
        let proceed: boolean;
        try {
          proceed = await beforeStart(sharing.signal);
        } finally {
          videoTracks.forEach((track) => track.removeEventListener('ended', onEnded));
        }
        const screenEnded = videoTracks.some((track) => track.readyState === 'ended');
        if (!proceed || screenEnded) {
          abandonCapture();
          return false;
        }
      }

      recorderRef.current = new RecordRTC(combinedStream, {
        type: 'video',
        mimeType: bestMimeType,
        videoBitsPerSecond: 1500000, // 1.5 Mbps — optimised for screencasting
        audioBitsPerSecond: 96000,   // 96 kbps — sufficient for voice narration
        canvas: {
          width: outputWidth,
          height: outputHeight,
        }
      });

      recorderRef.current.startRecording();
      isRecordingRef.current = true;
      setIsRecording(true);
      recordedMsRef.current = 0;
      setRecordingTime(0);
      startTimer();
      return true;

    } catch (err) {
      console.error('Recording error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start recording');
      // Don't leave the screen shared (or the mic / camera on) when nothing is recording.
      abandonCapture();
      return false;
    }
  }, [startTimer, releaseCapture, abandonCapture, audioConstraints, shouldCaptureSystemAudio]);

  const pauseRecording = useCallback(() => {
    if (recorderRef.current && isRecording && !isPaused) {
      recorderRef.current.pauseRecording();
      setIsPaused(true);
      stopTimer();
    }
  }, [isRecording, isPaused, stopTimer]);

  const resumeRecording = useCallback(() => {
    if (recorderRef.current && isRecording && isPaused) {
      recorderRef.current.resumeRecording();
      setIsPaused(false);
      startTimer();
    }
  }, [isRecording, isPaused, startTimer]);

  const stopRecording = useCallback((): Promise<Blob> => {
    // Stop button and the browser's "Stop sharing" can both fire: share one stop.
    if (stopPromiseRef.current) return stopPromiseRef.current;

    const promise = new Promise<Blob>((resolve, reject) => {
      try {
        // If already stopped, return the existing blob if available
        if (!isRecordingRef.current && recordedBlob) {
          return resolve(recordedBlob);
        }
        
        if (!recorderRef.current) {
          // If we have a recorded blob but no active recorder, return it
          if (recordedBlob) {
            return resolve(recordedBlob);
          }
          releaseCapture();
          return reject(new Error('No active recording'));
        }
        
        // Store the current recorder reference before cleanup
        const currentRecorder = recorderRef.current;
        
        // Stop camera preview and cleanup
        if (cameraStreamRef.current) {
          cameraStreamRef.current.getTracks().forEach(track => track.stop());
          cameraStreamRef.current = null;
        }
        

        let done = false;
        const finish = (blob: Blob) => {
          if (done) return;
          done = true;
          clearTimeout(stopTimeout);
          clearInterval(inactivePoll);
          setRecordedBlob(blob);
          isRecordingRef.current = false;
          setIsRecording(false);
          setIsPaused(false);
          setHasCamera(false);
          setHasMic(false);
          setCameraStream(null);
          stopTimer();

          // Clean up streams (recorded tracks + the raw screen capture and crop)
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          releaseCapture();

          // Only clear the recorder ref if it's still the current one
          if (recorderRef.current === currentRecorder) {
            recorderRef.current = null;
          }

          resolve(blob);
        };
        const fail = (err: unknown) => {
          if (done) return;
          done = true;
          clearTimeout(stopTimeout);
          clearInterval(inactivePoll);
          releaseCapture();
          reject(err);
        };
        const recordedSoFar = (): Blob | null => {
          try {
            const blob = currentRecorder.getBlob();
            return blob && blob.size > 0 ? blob : null;
          } catch {
            return null;
          }
        };

        // When every recorded track ends at once (capture stopped, no microphone), the
        // browser stops MediaRecorder itself and RecordRTC never calls the stop callback.
        // Take the finished recording in that case instead of waiting for the timeout.
        const nativeState = (): string | undefined => {
          try {
            const internal = currentRecorder.getInternalRecorder() as unknown as {
              getInternalRecorder?: () => MediaRecorder | undefined;
            };
            return internal?.getInternalRecorder?.()?.state;
          } catch {
            return undefined;
          }
        };
        const inactivePoll = setInterval(() => {
          if (nativeState() !== 'inactive') return;
          const blob = recordedSoFar();
          if (blob) finish(blob);
        }, 250);

        const stopTimeout = setTimeout(() => {
          const blob = recordedSoFar();
          if (blob) finish(blob);
          else fail(new Error('Recording stop timed out'));
        }, 5000); // 5 second timeout

        // Stop the recording; the capture is released afterwards so the last frames are kept.
        currentRecorder.stopRecording(() => {
          try {
            finish(currentRecorder.getBlob());
          } catch (err) {
            console.error('Error getting blob from recorder:', err);
            fail(err);
          }
        });
      } catch (err) {
        console.error('Error in stopRecording:', err);
        releaseCapture();
        reject(err);
      }
    });

    stopPromiseRef.current = promise;
    const clear = () => { stopPromiseRef.current = null; };
    promise.then(clear, clear);
    return promise;
  }, [recordedBlob, stopTimer, releaseCapture]);
  stopRecordingRef.current = stopRecording;

  /** Hides/shows the camera bubble in the recording (the camera itself keeps running). */
  const toggleCamera = useCallback(() => {
    if (!compositorRef.current) return;
    isCameraOnRef.current = !isCameraOnRef.current;
    setIsCameraOn(isCameraOnRef.current);
    applyBubbleVisibility();
  }, [applyBubbleVisibility]);

  /** Hide the drawn bubble while something else (a captured preview window) shows the camera. */
  const setBubbleSuppressed = useCallback((suppressed: boolean) => {
    bubbleSuppressedRef.current = suppressed;
    applyBubbleVisibility();
  }, [applyBubbleVisibility]);

  const toggleAudio = useCallback(() => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
        setIsMicOn(track.enabled);
      });
    }
  }, []);

  const resetRecording = useCallback(() => {
    // Clean up any existing streams
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
    releaseCapture();
    
    if (recorderRef.current) {
      recorderRef.current = null;
    }
    
    isRecordingRef.current = false;
    setHasCamera(false);
    setHasMic(false);
    setCameraStream(null);
    setCaptureSurface(null);
    stopTimer();
    recordedMsRef.current = 0;
    setRecordedBlob(null);
    setRecordingTime(0);
    setError(null);
    setIsRecording(false);
    setIsPaused(false);
  }, [stopTimer, releaseCapture]);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    isRecording,
    isPaused,
    recordedBlob,
    recordingTime: formatTime(recordingTime),
    recordingTimeSeconds: recordingTime, // raw integer seconds — use this when saving duration
    error,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetRecording,
    formatTime,
    isMicOn,
    hasMic,
    toggleAudio,
    hasCamera,
    isCameraOn,
    toggleCamera,
    cameraStream,
    captureSurface,
    setBubbleSuppressed,
  };
};